import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { assetUrl, lines } from "@/lib/api";
import { Rich, Reveal, Eyebrow } from "@/components/site/Rich";
import { ImageCarousel } from "@/components/site/ImageCarousel";

const H2 = ({ children, className = "", ...r }) => <h2 className={`font-serif font-bold text-3xl sm:text-4xl text-charcoal leading-tight ${className}`} {...r}>{children}</h2>;

export const FingerSection = ({ s }) => (
  <section className="py-20 lg:py-28 bg-ivory" data-testid="finger-section">
    <div className="max-w-[1280px] mx-auto px-6 grid gap-12 lg:grid-cols-2 items-center">
      <Reveal>
        <span className="block h-1 w-14 bg-brass mb-6" />
        <H2 data-testid="finger-title">{s.title}</H2>
        <Rich html={s.body} className="mt-6 text-charcoal/80" />
        <Link to="/digital-edition" className="mt-8 inline-flex items-center gap-2 text-brand font-semibold hover:text-brand-dark group" data-testid="finger-link">
          {s.link_label} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <Reveal delay={0.1}><img src={assetUrl(s.image)} alt={s.title} className="w-full h-auto rounded-2xl shadow-xl" /></Reveal>
    </div>
  </section>
);

export const PromoCards = ({ c }) => {
  const cards = [1, 2, 3].map((n) => ({ n, title: c[`card${n}_title`], body: c[`card${n}_body`], link: c[`card${n}_link`], image: c[`card${n}_image`] }));
  const to = { 1: "/digital-edition", 2: "/buy", 3: "/listing-options" };
  return (
    <section className="pb-20 lg:pb-28 bg-ivory" data-testid="promo-cards">
      <div className="max-w-[1280px] mx-auto px-6 grid gap-8 md:grid-cols-3">
        {cards.map((k, i) => (
          <Reveal key={k.n} delay={i * 0.1}>
            <article className="h-full flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300" data-testid={`promo-card-${k.n}`}>
              <img src={assetUrl(k.image)} alt={k.title} className="h-40 w-full object-cover" />
              <div className="p-7 flex-1 flex flex-col">
                <span className="block h-[3px] w-12 bg-brass mb-5" />
                <h3 className="font-serif text-2xl font-bold text-brand">{k.title}</h3>
                <Rich html={k.body} className="mt-4 text-sm text-charcoal/75 flex-1" />
                <Link to={to[k.n]} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand hover:text-brand-dark group" data-testid={`promo-card-link-${k.n}`}>
                  {k.link} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export const DeviceSection = ({ s }) => (
  <section className="py-20 lg:py-28 bg-ivory-alt" data-testid="device-section">
    <div className="max-w-[1280px] mx-auto px-6 grid gap-12 lg:grid-cols-2 items-center">
      <Reveal><img src={assetUrl(s.image)} alt={s.title} className="w-full h-auto rounded-2xl shadow-xl" /></Reveal>
      <Reveal delay={0.1}>
        <span className="block h-1 w-14 bg-brass mb-6" />
        <H2 data-testid="device-title">{s.title}</H2>
        <Rich html={s.body} className="mt-6 text-charcoal/80" />
        <div className="mt-8 flex flex-wrap gap-3">
          {lines(s.pills).map((p, i) => <span key={i} className="rounded-full bg-white border border-brass/50 px-5 py-2 text-sm font-semibold text-charcoal" data-testid={`device-pill-${i}`}>{p}</span>)}
        </div>
      </Reveal>
    </div>
  </section>
);

export const FeaturesSection = ({ f }) => (
  <section className="relative grain py-20 lg:py-28 bg-charcoal text-white" data-testid="features-section">
    <div className="relative max-w-[1280px] mx-auto px-6">
      <Eyebrow>{f.eyebrow}</Eyebrow>
      <h2 className="mt-3 font-serif font-bold text-3xl sm:text-4xl" data-testid="features-title">{f.title}</h2>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {[1, 2, 3].map((n, i) => (
          <Reveal key={n} delay={i * 0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-8 hover:border-brass/60 transition-colors duration-300" data-testid={`feature-card-${n}`}>
              <span className="h-11 w-11 rounded-lg bg-brand flex items-center justify-center"><Check className="h-5 w-5" /></span>
              <h3 className="mt-6 font-serif text-xl font-bold text-brass">{f[`f${n}_title`]}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/75">{f[`f${n}_text`]}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Link to="/listing-options" className="mt-12 inline-flex rounded-full bg-brand hover:bg-brand-dark px-7 py-3 text-sm font-semibold transition-colors" data-testid="features-list-button">{f.button}</Link>
    </div>
  </section>
);

export const StatementBand = ({ s }) => {
  const parts = s.brand && s.text.includes(s.brand) ? s.text.split(s.brand) : [s.text];
  return (
    <section className="bg-white py-16 lg:py-20" data-testid="statement-band">
      <Reveal className="max-w-4xl mx-auto px-6 text-center">
        <p className="font-serif font-bold text-xl sm:text-2xl lg:text-[1.7rem] leading-snug text-charcoal">
          {parts.map((p, i) => (
            <span key={i}>{p}{i < parts.length - 1 && <span className="text-brand">{s.brand}</span>}</span>
          ))}
        </p>
      </Reveal>
    </section>
  );
};

export const CarouselBand = ({ title, images, id, className }) => (
  <section className={`py-16 lg:py-20 ${className}`} data-testid={`${id}-section`}>
    <h2 className="font-serif font-bold text-2xl sm:text-3xl text-center text-charcoal mb-10 px-6" data-testid={`${id}-title`}>{title}</h2>
    <ImageCarousel images={images} id={id} />
  </section>
);
